import React, { useState, useEffect, useMemo, useRef } from "react";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AlertCircle, CheckCircle2, Info, XCircle } from "lucide-react";
import type { BMICaptureNode as BMICaptureNodeType } from "@/lib/pharmacyFirst/types";
import { mapEthnicityToAdjustmentGroup } from "@/lib/pharmacyFirst/pathways/aht/weight-selection.pathway";
import {
  HEIGHT_CM_RANGE,
  HEIGHT_IN_RANGE,
  MEASUREMENT_SOURCES,
  MEASUREMENT_SOURCE_LABELS,
  WEIGHT_KG_RANGE,
  WEIGHT_LB_RANGE,
  fmt,
  inRange,
  inclusionResult,
  lastRecordedLine,
  measurementLooksWrong,
  type MeasurementSource,
} from "../fields/weightBmiEntry";

export interface BMICaptureAnswer {
  heightCm: number;
  weightKg: number;
  bmi: number;
  measurementSource?: MeasurementSource;
}

interface LastRecorded {
  heightCm?: number;
  weightKg?: number;
  recordedAt?: string;
}

interface BMICaptureNodeProps {
  node: BMICaptureNodeType;
  isDarkMode?: boolean;
  stepNumber?: number;
  onAnswer: (answer: BMICaptureAnswer) => void;
  /** Previously given answer, re-seeded when the pharmacist steps back. */
  value?: BMICaptureAnswer | null;
  patientEthnicAdjustmentGroup?: string;
  /** Existing record values, shown above the fields. Never used to fill them in. */
  lastRecorded?: LastRecorded;
  /** Show the meets / below inclusion result under the BMI (adult initial and transfer routes). */
  showInclusionResult?: boolean;
}

type HeightUnit = "cm" | "ft/in";
type WeightUnit = "kg" | "st/lbs";

interface Measurement {
  status: "empty" | "invalid" | "ok";
  error?: string;
}

const ADJUSTED_GROUPS = [
  "south_asian",
  "chinese",
  "other_asian",
  "middle_eastern",
  "black_african",
  "african_caribbean",
];

const DECIMAL = /^\d+(\.\d+)?$/;
const WHOLE = /^\d+$/;
const clean = (s: string) => s.trim().replace(",", ".");

function cmToFtIn(cm: number): { feet: number; inches: number } {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round((totalInches % 12) * 10) / 10;
  return { feet, inches };
}

function ftInToCm(feet: number, inches: number): number {
  return (feet * 12 + inches) * 2.54;
}

function kgToStLbs(kg: number): { stone: number; pounds: number } {
  const totalLbs = kg * 2.20462;
  const stone = Math.floor(totalLbs / 14);
  const pounds = Math.round((totalLbs % 14) * 10) / 10;
  return { stone, pounds };
}

function stLbsToKg(stone: number, pounds: number): number {
  return (stone * 14 + pounds) / 2.20462;
}

function calculateBMI(heightCm: number, weightKg: number): number | null {
  if (heightCm <= 0 || weightKg <= 0) return null;
  const heightM = heightCm / 100;
  return Math.round((weightKg / (heightM * heightM)) * 10) / 10;
}

function scrollPanelsToTop(from: HTMLElement | null) {
  for (let node = from?.parentElement ?? null; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") node.scrollTop = 0;
  }
  window.scrollTo?.(0, 0);
}

export function BMICaptureNode({
  node,
  isDarkMode = false,
  stepNumber,
  onAnswer,
  value,
  patientEthnicAdjustmentGroup,
  lastRecorded,
  showInclusionResult = false,
}: BMICaptureNodeProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [heightUnit, setHeightUnit] = useState<HeightUnit>("cm");
  const [weightUnit, setWeightUnit] = useState<WeightUnit>("kg");

  const [heightCmText, setHeightCmText] = useState("");
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");

  const [weightKgText, setWeightKgText] = useState("");
  const [weightStone, setWeightStone] = useState("");
  const [weightPounds, setWeightPounds] = useState("");

  // Precise value carried across a unit switch, so rounding the displayed number never changes the BMI.
  const [exactHeightCm, setExactHeightCm] = useState<number | null>(null);
  const [exactWeightKg, setExactWeightKg] = useState<number | null>(null);

  const [source, setSource] = useState<MeasurementSource | "">("");
  const [shown, setShown] = useState({ height: false, weight: false });
  const hide = (f: "height" | "weight") => setShown((s) => (s[f] ? { ...s, [f]: false } : s));
  const reveal = (f: "height" | "weight") => setShown((s) => (s[f] ? s : { ...s, [f]: true }));

  useEffect(() => scrollPanelsToTop(rootRef.current), []);

  const resolvedEthnicGroup = patientEthnicAdjustmentGroup
    ? (ADJUSTED_GROUPS.includes(patientEthnicAdjustmentGroup)
        ? patientEthnicAdjustmentGroup
        : mapEthnicityToAdjustmentGroup(patientEthnicAdjustmentGroup))
    : undefined;
  const isAdjusted = !!resolvedEthnicGroup && ADJUSTED_GROUPS.includes(resolvedEthnicGroup);
  const groupLabel = (resolvedEthnicGroup ?? patientEthnicAdjustmentGroup ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  // Only the pharmacist's own earlier answer refills the fields; the patient record is shown, never prefilled.
  useEffect(() => {
    setHeightCmText(value?.heightCm && value.heightCm > 0 ? fmt(value.heightCm) : "");
    setHeightFeet("");
    setHeightInches("");
    setWeightKgText(value?.weightKg && value.weightKg > 0 ? fmt(value.weightKg) : "");
    setWeightStone("");
    setWeightPounds("");
    setHeightUnit("cm");
    setWeightUnit("kg");
    setExactHeightCm(value?.heightCm ?? null);
    setExactWeightKg(value?.weightKg ?? null);
    setSource(value?.measurementSource ?? "");
    setShown({ height: false, weight: false });
  }, [node.id, value?.heightCm, value?.weightKg, value?.measurementSource]);

  const height = useMemo<Measurement & { cm?: number }>(() => {
    if (heightUnit === "cm") {
      const raw = clean(heightCmText);
      if (raw === "") return { status: "empty" };
      if (!DECIMAL.test(raw)) return { status: "invalid", error: "Enter height as a number, in centimetres" };
      const cm = exactHeightCm ?? Number(raw);
      return inRange(cm, HEIGHT_CM_RANGE)
        ? { status: "ok", cm }
        : { status: "invalid", cm, error: measurementLooksWrong("Height", `${heightCmText.trim()} cm`) };
    }
    const ft = clean(heightFeet);
    const ins = clean(heightInches);
    if (ft === "" && ins === "") return { status: "empty" };
    if ((ft !== "" && !WHOLE.test(ft)) || (ins !== "" && !DECIMAL.test(ins))) {
      return { status: "invalid", error: "Enter height as numbers, in feet and inches" };
    }
    const totalIn = (ft === "" ? 0 : Number(ft)) * 12 + (ins === "" ? 0 : Number(ins));
    const cm = exactHeightCm ?? ftInToCm(ft === "" ? 0 : Number(ft), ins === "" ? 0 : Number(ins));
    const inches = exactHeightCm !== null ? exactHeightCm / 2.54 : totalIn;
    return inRange(inches, HEIGHT_IN_RANGE)
      ? { status: "ok", cm }
      : {
          status: "invalid",
          cm,
          error: measurementLooksWrong("Height", `${ft === "" ? 0 : ft} ft ${ins === "" ? 0 : ins} in`),
        };
  }, [heightUnit, heightCmText, heightFeet, heightInches, exactHeightCm]);

  const weight = useMemo<Measurement & { kg?: number }>(() => {
    if (weightUnit === "kg") {
      const raw = clean(weightKgText);
      if (raw === "") return { status: "empty" };
      if (!DECIMAL.test(raw)) return { status: "invalid", error: "Enter weight as a number, in kilograms" };
      const kg = exactWeightKg ?? Number(raw);
      return inRange(kg, WEIGHT_KG_RANGE)
        ? { status: "ok", kg }
        : { status: "invalid", kg, error: measurementLooksWrong("Weight", `${weightKgText.trim()} kg`) };
    }
    const st = clean(weightStone);
    const lbs = clean(weightPounds);
    if (st === "" && lbs === "") return { status: "empty" };
    if ((st !== "" && !WHOLE.test(st)) || (lbs !== "" && !DECIMAL.test(lbs))) {
      return { status: "invalid", error: "Enter weight as numbers, in stone and pounds" };
    }
    const totalLbs = (st === "" ? 0 : Number(st)) * 14 + (lbs === "" ? 0 : Number(lbs));
    const kg = exactWeightKg ?? stLbsToKg(st === "" ? 0 : Number(st), lbs === "" ? 0 : Number(lbs));
    const pounds = exactWeightKg !== null ? exactWeightKg * 2.20462 : totalLbs;
    return inRange(pounds, WEIGHT_LB_RANGE)
      ? { status: "ok", kg }
      : {
          status: "invalid",
          kg,
          error: measurementLooksWrong("Weight", `${st === "" ? 0 : st} st ${lbs === "" ? 0 : lbs} lbs`),
        };
  }, [weightUnit, weightKgText, weightStone, weightPounds, exactWeightKg]);

  const ready = height.status === "ok" && weight.status === "ok";
  const bmi = ready ? calculateBMI(height.cm!, weight.kg!) : null;

  const blockedReason = (() => {
    const bad = [height.status === "invalid" ? "height" : "", weight.status === "invalid" ? "weight" : ""].filter(Boolean);
    if (bad.length) return `Check the ${bad.join(" and ")} before continuing.`;
    if (!ready) return "Enter height and weight to continue.";
    if (source === "") return "Select how height and weight were obtained to continue.";
    return "";
  })();
  const canSave = blockedReason === "" && bmi !== null;

  const inclusion =
    showInclusionResult && bmi !== null ? inclusionResult(bmi, isAdjusted ? groupLabel : undefined) : null;

  const handleHeightUnitSwitch = (unit: HeightUnit) => {
    if (unit === heightUnit) return;
    const cm = height.cm;
    if (cm !== undefined) {
      setExactHeightCm(cm);
      if (unit === "cm") {
        setHeightCmText(fmt(cm));
      } else {
        const whole = Math.round(cm / 2.54);
        setHeightFeet(String(Math.floor(whole / 12)));
        setHeightInches(String(whole % 12));
      }
    }
    setHeightUnit(unit);
  };

  const handleWeightUnitSwitch = (unit: WeightUnit) => {
    if (unit === weightUnit) return;
    const kg = weight.kg;
    if (kg !== undefined) {
      setExactWeightKg(kg);
      if (unit === "kg") {
        setWeightKgText(fmt(kg));
      } else {
        const { stone, pounds } = kgToStLbs(kg);
        setWeightStone(String(stone));
        setWeightPounds(String(pounds));
      }
    }
    setWeightUnit(unit);
  };

  const handleContinue = () => {
    if (!canSave || bmi === null || source === "") {
      setShown({ height: true, weight: true });
      return;
    }
    onAnswer({
      heightCm: Math.round(height.cm! * 10) / 10,
      weightKg: Math.round(weight.kg! * 10) / 10,
      bmi,
      measurementSource: source,
    });
  };

  const hasRecord = lastRecorded?.heightCm !== undefined || lastRecorded?.weightKg !== undefined;
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const pillBase = `px-3 py-1 rounded-full text-sm font-medium border transition-colors`;
  const pillActive = isDarkMode
    ? "bg-white text-black border-white"
    : "bg-gray-900 text-white border-gray-900";
  const pillInactive = isDarkMode
    ? "bg-transparent text-gray-400 border-gray-600 hover:border-gray-400"
    : "bg-transparent text-gray-600 border-gray-300 hover:border-gray-500";
  const labelClass = `text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;
  const inputClass = `w-full ${isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-400" : "!bg-white !border-gray-300 !text-gray-900 placeholder:!text-gray-500"}`;

  const heightInvalid = height.status === "invalid" && shown.height;
  const weightInvalid = weight.status === "invalid" && shown.weight;
  const heightHint = heightUnit === "cm" ? "In centimetres" : "In feet and inches";
  const weightHint = weightUnit === "kg" ? "In kilograms" : "In stone and pounds";

  const fieldError = (id: string, message?: string) =>
    message ? (
      <p id={id} data-testid={id} className="flex items-start gap-1.5 text-sm text-red-700">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{message}</span>
      </p>
    ) : null;

  const unitPills = <T extends string>(units: readonly T[], current: T, label: string, onPick: (u: T) => void) => (
    <div className="flex gap-1" role="group" aria-label={label}>
      {units.map((u) => (
        <button
          key={u}
          type="button"
          aria-pressed={current === u}
          onClick={() => onPick(u)}
          className={`${pillBase} ${current === u ? pillActive : pillInactive}`}
        >
          {u}
        </button>
      ))}
    </div>
  );

  const textInput = (props: {
    id: string;
    label: string;
    value: string;
    onChange: (v: string) => void;
    invalid: boolean;
    describedBy: string;
    field: "height" | "weight";
  }) => (
    <Input
      id={props.id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      aria-label={props.label}
      aria-invalid={props.invalid || undefined}
      aria-describedby={props.describedBy}
      value={props.value}
      onChange={(e) => {
        hide(props.field);
        props.onChange(e.target.value);
      }}
      onBlur={() => reveal(props.field)}
      className={`${inputClass} ${props.invalid ? "!border-red-500" : ""}`}
    />
  );

  return (
    <div
      ref={rootRef}
      className={`border rounded-2xl p-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-center gap-3 mb-4">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
        )}
        <div className="flex-1">
          <h3
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {node.text}
          </h3>
          {node.hint && (
            <p
              className={`text-xs mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {node.hint}
            </p>
          )}
        </div>
      </div>

      <p data-testid="bmi-last-recorded" className={`text-sm mb-4 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
        {lastRecordedLine(lastRecorded)}
      </p>

      {/* Height */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="bmi-height" className={labelClass}>Height</Label>
          {unitPills(["cm", "ft/in"] as const, heightUnit, "Height unit", handleHeightUnitSwitch)}
        </div>
        <p id="bmi-height-hint" className={`text-xs ${muted}`}>{heightHint}</p>
        {heightUnit === "cm" ? (
          textInput({
            id: "bmi-height",
            label: "Height in centimetres",
            value: heightCmText,
            field: "height",
            invalid: heightInvalid,
            describedBy: `bmi-height-hint${heightInvalid ? " bmi-height-error" : ""}`,
            onChange: (v) => {
              setExactHeightCm(null);
              setHeightCmText(v);
            },
          })
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <div>
              {textInput({
                id: "bmi-height",
                label: "Height, feet",
                value: heightFeet,
                field: "height",
                invalid: heightInvalid,
                describedBy: `bmi-height-hint${heightInvalid ? " bmi-height-error" : ""}`,
                onChange: (v) => {
                  setExactHeightCm(null);
                  setHeightFeet(v);
                },
              })}
              <p className={`text-xs mt-1 ${muted}`}>ft</p>
            </div>
            <div>
              {textInput({
                id: "bmi-height-inches",
                label: "Height, inches",
                value: heightInches,
                field: "height",
                invalid: heightInvalid,
                describedBy: `bmi-height-hint${heightInvalid ? " bmi-height-error" : ""}`,
                onChange: (v) => {
                  setExactHeightCm(null);
                  setHeightInches(v);
                },
              })}
              <p className={`text-xs mt-1 ${muted}`}>in</p>
            </div>
          </div>
        )}
        {fieldError("bmi-height-error", shown.height ? height.error : undefined)}
      </div>

      {/* Weight */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="bmi-weight" className={labelClass}>Weight</Label>
          {unitPills(["kg", "st/lbs"] as const, weightUnit, "Weight unit", handleWeightUnitSwitch)}
        </div>
        <p id="bmi-weight-hint" className={`text-xs ${muted}`}>{weightHint}</p>
        {weightUnit === "kg" ? (
          textInput({
            id: "bmi-weight",
            label: "Weight in kilograms",
            value: weightKgText,
            field: "weight",
            invalid: weightInvalid,
            describedBy: `bmi-weight-hint${weightInvalid ? " bmi-weight-error" : ""}`,
            onChange: (v) => {
              setExactWeightKg(null);
              setWeightKgText(v);
            },
          })
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <div>
              {textInput({
                id: "bmi-weight",
                label: "Weight, stone",
                value: weightStone,
                field: "weight",
                invalid: weightInvalid,
                describedBy: `bmi-weight-hint${weightInvalid ? " bmi-weight-error" : ""}`,
                onChange: (v) => {
                  setExactWeightKg(null);
                  setWeightStone(v);
                },
              })}
              <p className={`text-xs mt-1 ${muted}`}>st</p>
            </div>
            <div>
              {textInput({
                id: "bmi-weight-pounds",
                label: "Weight, pounds",
                value: weightPounds,
                field: "weight",
                invalid: weightInvalid,
                describedBy: `bmi-weight-hint${weightInvalid ? " bmi-weight-error" : ""}`,
                onChange: (v) => {
                  setExactWeightKg(null);
                  setWeightPounds(v);
                },
              })}
              <p className={`text-xs mt-1 ${muted}`}>lbs</p>
            </div>
          </div>
        )}
        {fieldError("bmi-weight-error", shown.weight ? weight.error : undefined)}
      </div>

      {/* Live BMI */}
      {bmi !== null && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-center ${
            isDarkMode ? "bg-gray-700" : "bg-gray-100"
          }`}
        >
          <p className={`text-xs mb-0.5 ${muted}`}>Calculated BMI</p>
          <p
            data-testid="bmi-value"
            className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {bmi.toFixed(1)} <span className="text-sm font-normal">kg/m²</span>
          </p>
          {inclusion && (
            <p
              role="status"
              data-testid="bmi-inclusion-result"
              data-state={inclusion.state}
              className={`mt-2 flex items-start justify-center gap-1.5 text-sm font-medium text-left ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
            >
              {inclusion.state === "meets" ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />
              ) : inclusion.state === "below" ? (
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-700" aria-hidden="true" />
              ) : (
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
              )}
              <span>
                {inclusion.text}
                {inclusion.detail && <span className="block font-normal mt-1">{inclusion.detail}</span>}
              </span>
            </p>
          )}
        </div>
      )}

      {/* Inclusion threshold panel */}
      <div
        className={`mb-4 px-3 py-2 rounded-lg text-sm ${
          isDarkMode
            ? "bg-gray-700 text-gray-300"
            : "bg-gray-50 text-gray-600"
        }`}
      >
        <p className="font-medium mb-0.5">
          Inclusion threshold for this patient:
        </p>
        {isAdjusted ? (
          <>
            <p>BMI ≥27.5 kg/m²</p>
            <p className={`text-xs mt-0.5 ${muted}`}>
              {groupLabel} — adjusted threshold applies
            </p>
          </>
        ) : (
          <>
            <p>BMI ≥30 kg/m² (standard)</p>
            <p className={`text-xs mt-0.5 ${muted}`}>
              Or ≥27 with ≥1 weight-related comorbidity
            </p>
          </>
        )}
      </div>

      <fieldset className="mb-4 space-y-2" data-testid="bmi-measurement-source">
        <legend className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
          How were height and weight obtained?
        </legend>
        <RadioGroup
          value={source}
          onValueChange={(v) => setSource(v as MeasurementSource)}
          aria-label="How were height and weight obtained?"
          className="space-y-2"
        >
          {MEASUREMENT_SOURCES.map((id) => {
            const unavailable = id === "record" && !hasRecord;
            const recordDate =
              id === "record" && lastRecorded?.recordedAt && !Number.isNaN(Date.parse(lastRecorded.recordedAt))
                ? new Date(lastRecorded.recordedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                : null;
            return (
              <label
                key={id}
                htmlFor={`bmi-source-${id}`}
                className={`flex items-center gap-2 rounded-lg border p-3 transition ${unavailable ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${
                  source === id
                    ? isDarkMode ? "border-blue-500 bg-blue-950" : "border-blue-500 bg-blue-50"
                    : isDarkMode ? "border-gray-600" : "border-gray-200 bg-white"
                }`}
              >
                <RadioGroupItem value={id} id={`bmi-source-${id}`} disabled={unavailable} />
                <span className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}>
                  {MEASUREMENT_SOURCE_LABELS[id]}
                  {recordDate && <span className={`ml-2 text-xs ${muted}`}>(recorded {recordDate})</span>}
                  {unavailable && <span className={`ml-2 text-xs ${muted}`}>(nothing on record)</span>}
                </span>
              </label>
            );
          })}
        </RadioGroup>
      </fieldset>

      <FlowPrimaryButton
        label="Save and continue"
        onClick={handleContinue}
        disabled={!canSave}
        blockedReason={blockedReason || "Enter height and weight to continue."}
        size="lg"
        className="w-full"
      />
    </div>
  );
}
